import _, { each } from 'underscore';
import * as putil from '@bingads-webui-universal/primitive-utilities';
import { partialConversionPairs } from '@bingads-webui-campaign/column-conversions';
import { initialize as initializeSOV, updateEntitySchemaForSOV } from './update-entity-schema-for-sov';
import { initialize as initializeCustomColumn, updateEntitySchemaForCustomColumn } from './update-entity-schema-for-custom-column';
import { initialize as initializeAveragePosition, updateEntitySchemaForRemovedPerformanceMetrics } from './update-entity-schema-for-removed-performance-metrics';
import { initialize as initializeAudienceSOV, enableForAudienceSOVFeatures } from './update-entity-schema-for-audiencesov';

export {
  updateEntitySchemaForSOV,
  updateEntitySchemaForCustomColumn,
  updateEntitySchemaForRemovedPerformanceMetrics,
  enableForAudienceSOVFeatures,
};

let globalConfig;
let Permissions = null;

export function initialize(options) {
  ({ globalConfig } = options);
  Permissions = globalConfig.get('Permissions');
  initializeSOV(options);
  initializeCustomColumn(options);
  initializeAveragePosition(options);
  initializeAudienceSOV(options);
}

// get whether feature is enabled by property name
function getFeaturePermissionByPropertyName(propertyName) {
  switch (propertyName.toLowerCase()) {
    case 'campaigntype':
      return !Permissions.IsEnhancedShoppingCampaignAllowed &&
        !Permissions.IsDynamicSearchAdsV1Enabled &&
        !Permissions.IsAIMCampaignEnabled;

    case 'language':
      return Permissions.IsCampaignMultipleLanguageEnabled;

    case 'associationscount':
    case 'trackingstatus':
      return Permissions.IsAudienceGridNewColumnsSupported;

    case 'isexperiment':
      return Permissions.IsExperimentsEnabled;

    case 'adsbybing':
      return Permissions.IsAdsByBingEnabled;

    case 'allconv':
      return true; // [hagu1]todo: skip this method for 'allconv'

    case 'viewthrough':
      return Permissions.IsViewThroughConversionEnabled;

    case 'viewthroughrevenue':
      return Permissions.IsViewThroughConversionsRevenueEnabled;

    case 'avgpositiondeprecationphase1':
      return Permissions.IsAvgPositionDeprecationPhase1Enabled;

    case 'cpmbid':
      return Permissions.IsManualCpmEnabled;

    case 'cpvbid':
      return Permissions.IsVideoAdsForCampaignEnabled;

    case 'videoads':
      return Permissions.IsVideoAdsPerfMetricsEnabled;

    case 'appcampaign':
      return _.result(Permissions.dynamic, 'AppCampaignNewCampaignGridColumns');

    // Add BidStrategyName filter to campaign grid
    case 'bidstrategyname':
      return _.result(Permissions.dynamic, 'PortfolioBidStrategy');

    case 'cpsreporting':
      return _.result(Permissions.dynamic, 'CPSReporting');

    case 'installcolumns':
      return _.result(Permissions.dynamic, 'InstallPerformanceColumns');

    case 'campaignmigrationstatus':
      return true; // GA

    case 'conversionvaluerule':
      return _.result(Permissions.dynamic, 'ConvValueRules');

    default:
      return false;
  }
}

function removeEnumValuesFromProperty(schema, propertyName, removeEnumArray) {
  const schemaCopy = schema;
  if (!_.isEmpty(schema.properties[propertyName])) {
    schemaCopy.properties[propertyName].enum =
      _.difference(schema.properties[propertyName].enum, removeEnumArray);
  }

  return schemaCopy;
}

function updateEnumValueSets(schema) {
  const entityName = schema.name.toLowerCase();

  // Listing here all the enum valus to remove in different pilots (or always remove)
  const disabledTargets = [
    {
      disabled: !Permissions.IsDynamicSearchAdsV1Enabled && (entityName === 'ad'),
      property: 'Type',
      enumValueSets: ['DynamicSearch'], // this will not used in any where in filter options
    },
    {
      disabled: entityName === 'campaign',
      property: 'CampaignType',
      enumValueSets: ['DynamicSearchAds'], // DynamicSearchAds campaign type is deprecated, will not used in any where in filter options
    },
    {
      // Bug 1692445: <AD-Mar-19>BetaProd: [Campaigns] Some check box conditions
      // are not shown for filter of Campaign status/Campaign type/Bid strategy type on prod.
      // This should be controlled by pilot
      disabled: !Permissions.IsSmartCampaignPhase2Enabled,
      property: 'CampaignType',
      enumValueSets: ['Smart'],
    },
    {
      disabled: !(Permissions.dynamic && Permissions.dynamic.HotelCampaign),
      property: 'CampaignType',
      enumValueSets: ['Hotel'],
    },
    {
      disabled: !(Permissions.dynamic && Permissions.dynamic.PerformanceMaxCampaigns),
      property: 'CampaignType',
      enumValueSets: ['PerformanceMax'],
    },
    {
      disabled: entityName === 'campaign',
      property: 'Status',
      enumValueSets: ['InProgress'], // remove this when there are localized strings for this enum value
    },
    {
      disabled: true,
      property: 'Delivery',
      enumValueSets: ['CampaignInProgress'],
    },
    {
      disabled: true,
      property: 'DeliveryStatus',
      enumValueSets: ['CampaignInProgress'],
    },
    {
      disabled: true,
      property: 'Type',
      enumValueSets: ['HotelBookingGoal'],
    },
    {
      disabled: true,
      property: 'DisplayBiddingStrategyType',
      enumValueSets: ['ManualCpi'],
    },
    {
      disabled: true,
      property: 'DisplayBiddingStrategyType',
      enumValueSets: ['InheritFromParent', 'MaxRoas', 'TargetCostPerSale'],
    },
    {
      disabled: !(Permissions.IsCampaignTargetROASBidStrategyEnabled ||
        Permissions.IsNewCampaignTargetROASBidStrategyEnabled),
      property: 'DisplayBiddingStrategyType',
      enumValueSets: ['TargetRoas'],
    },
    {
      disabled: !Permissions.IsMaximizeConversionValueEnabled && !Permissions.IsSmartShoppingCampaignEnabled, // eslint-disable-line
      property: 'DisplayBiddingStrategyType',
      enumValueSets: ['MaxConversionValue'],
    },
    {
      disabled: !Permissions.IsTargetImpressionShareEnabled,
      property: 'DisplayBiddingStrategyType',
      enumValueSets: ['TargetImpressionShare'],
    },
    {
      disabled: !Permissions.IsCostPerSaleEnabled,
      property: 'DisplayBiddingStrategyType',
      enumValueSets: ['CostPerSale'],
    },
    {
      disabled: !(Permissions.dynamic && Permissions.dynamic.HotelCampaign),
      property: 'DisplayBiddingStrategyType',
      enumValueSets: ['PercentCpc', 'Commission'],
    },
    {
      disabled: !Permissions.IsAIMCampaignEnabled && (entityName === 'campaign'),
      property: 'CampaignType',
      enumValueSets: ['Audience'], // remove audience campaign from other entity
    },
    {
      disabled: !Permissions.IsAdsGlobalizationPhase1Enabled,
      property: 'Language',
      enumValueSets: ['Greek', 'Polish', 'Bulgarian', 'Croatian', 'Czech', 'Estonian', 'Hungarian', 'Latvian', 'Lithuanian', 'Maltese', 'Romanian', 'Slovak', 'Slovenian'],
    },
    {
      disabled: !Permissions.IsAdsGlobalizationPhase2Enabled,
      property: 'Language',
      enumValueSets: ['Turkish', 'Serbian', 'Bosnian', 'Albanian', 'Macedonian', 'Icelandic'],
    },
    {
      disabled: !Permissions.IsAdsGlobalizationPhase6Enabled,
      property: 'Language',
      enumValueSets: ['Hebrew', 'Arabic', 'Russian'],
    },
    {
      disabled: !Permissions.IsAdsGlobalizationPhase2JapanEnabled,
      property: 'Language',
      enumValueSets: ['Japanese'],
    },
    {
      disabled: !Permissions.IsAdsGlobalizationPhase9Enabled,
      property: 'Language',
      enumValueSets: ['Hindi', 'Malay', 'Thai', 'Indonesian', 'Tagalog'],
    },
    {
      disabled: !Permissions.IsAdsGlobalizationPhase9VIEnabled,
      property: 'Language',
      enumValueSets: ['Vietnamese'],
    },
  ];

  _.chain(disabledTargets)
    .filter({ disabled: true })
    .each((target) => {
      removeEnumValuesFromProperty(schema, target.property, target.enumValueSets);
    });
}


export function enableForExperimentFeature(schema, scopeable) {
  const isInPilot = Permissions.IsExperimentsEnabled;
  const isAccountScope = !globalConfig.get('CurrentCampaign');
  const campaignProperty = schema.properties.Campaign;
  const isExperimentProperty = putil.get(campaignProperty, 'schema.properties.IsExperiment');

  if (isInPilot && (!scopeable || isAccountScope) && campaignProperty && isExperimentProperty) {
    if (campaignProperty.filterable) {
      campaignProperty.filterable = _.union(campaignProperty.filterable, ['IsExperiment']);
    }
    isExperimentProperty.filterable = true;
  }
}

function updateFilterableForExtraPerformanceMetrics(schema, metricNameArray, isEnabling) {
  const schemaCopy = schema;
  const performanceMetrics = putil.get(schemaCopy, 'properties.PerformanceMetrics');
  const performanceMetricsProperties = putil.get(performanceMetrics, 'schema.properties');
  if (performanceMetricsProperties) {
    if (_.isArray(performanceMetrics.filterable)) {
      performanceMetrics.filterable = isEnabling ?
        _.union(performanceMetrics.filterable, metricNameArray) :
        _.difference(performanceMetrics.filterable, metricNameArray);
    }
    _.each(performanceMetricsProperties, (prop, propName) => {
      if (_.contains(metricNameArray, propName)) {
        // eslint-disable-next-line no-param-reassign
        prop.filterable = isEnabling;
      }
    });
  }
  return schemaCopy;
}

// Add BidStrategyName filter to ad group grid and keyword grid
function enableForPortfolioBidStrategyFeature(schema) {
  const isInPilot = _.result(Permissions.dynamic, 'PortfolioBidStrategy');
  const isAllowedEntity = _.contains(['AdGroup', 'Keyword'], schema.name);
  const campaignProperty = schema.properties.Campaign;
  const bidStrategyNameProperty = putil.get(campaignProperty, 'schema.properties.BidStrategyName');

  if (isInPilot && isAllowedEntity && campaignProperty && bidStrategyNameProperty) {
    if (campaignProperty.filterable) {
      campaignProperty.filterable = _.union(campaignProperty.filterable, ['BidStrategyName']);
    }
    bidStrategyNameProperty.filterable = true;
  }
}


// This is used in automated rule page. Add filter for "Insights".
// Enum values should be different from those in performance/campaigns grid
function enableAndUpdateFilterForAvailableInsightTypeIds(schema) {
  const isAllowedEntity = _.contains(['Campaign'], schema.name);
  const schemaCopy = schema;

  if (isAllowedEntity && schemaCopy.properties.AvailableInsightTypeIds) {
    schemaCopy.properties.AvailableInsightTypeIds = {
      $ref: 'Enum/FluctuationType',
      property: 'AvailableInsightTypeIds',
      type: 'string',
      filterable: true,
      enumType: 'FluctuationType',
      enum: ['Click', 'Impression', 'Spend'],
      chooseType: 'checkbox',
      operators: ['contains'],
    };
  }
}

export function enableFilterForExtraPerformanceMetrics(schema, metricNameArray) {
  return updateFilterableForExtraPerformanceMetrics(schema, metricNameArray, true);
}

function enableFilterForPilotedPerformanceMetrics(schema, pilotName, metricNameArray) {
  const isInPilot = getFeaturePermissionByPropertyName(pilotName);
  return updateFilterableForExtraPerformanceMetrics(schema, metricNameArray, isInPilot);
}


function enableForViewThroughConversionsFeature(schema) {
  enableFilterForPilotedPerformanceMetrics(schema, 'viewthrough', ['ViewThroughConversions']);
  enableFilterForPilotedPerformanceMetrics(schema, 'viewthroughrevenue', [
    'ViewThroughConversionsRevenue',
    'ViewThroughConversionsCPA',
    'ViewThroughConversionsReturnOnAdSpend',
    'ViewThroughConversionsRate',
  ]);
  return schema;
}

function enableForConversionValueRuleFeature(schema) {
  enableFilterForPilotedPerformanceMetrics(
    schema, 'conversionvaluerule',
    ['AdvertiserReportedRevenueAdjustment', 'AllConversionAdvertiserReportedRevenueAdjustment', 'ViewThroughConversionsRevenueAdjustment']
  );
  return schema;
}

function updateEntitySchemaForMainGoalSelectionFeature(schema) {
  enableFilterForPilotedPerformanceMetrics(schema, 'allconv', [
    'AllConversionCPA',
    'AllConversions',
    'AllConversionRate',
    'AllConversionAdvertiserReportedRevenue',
    'AllConversionRevenueOnAdSpend']);
  return schema;
}

function updateEntitySchemaForVideoAdsFeature(schema) {
  const pilotGridNameArray = ['Account', 'Campaign', 'AdGroup', 'Ad'];
  let pilotProperty = '';
  if (_.contains(pilotGridNameArray, schema.name)) { pilotProperty = 'videoads'; }

  enableFilterForPilotedPerformanceMetrics(schema, pilotProperty, [
    'VideoViews',
    'ViewThroughRate',
    'AverageCPV',
    'TotalWatchTimeInMS',
    'AverageWatchTimePerImpression',
    'AverageWatchTimePerVideoView',
    'VideoViewsAt25Percent',
    'VideoViewsAt50Percent',
    'VideoViewsAt75Percent',
    'CompletedVideoViews',
    'VideoCompletionRate',
  ]);
  return schema;
}

function updateEntitySchemaForAppCampaign(schema) {
  const pilotGridNameArray = ['Campaign'];
  let pilotProperty = '';
  if (_.contains(pilotGridNameArray, schema.name)) { pilotProperty = 'appcampaign'; }

  enableFilterForPilotedPerformanceMetrics(schema, pilotProperty, [
    'Downloads',
    'FirstLaunches',
    'Purchases',
    'Subscriptions',
    'PostClickDownloadRate',
    'CostPerDownload',
    'PostClickFirstLaunchRate',
    'CostPerFirstLaunch',
    'PostInstallPurchaseRate',
    'CostPerPurchase',
    'PostInstallSubscriptionRate',
    'CostPerSubscription',
  ]);
  return schema;
}

function updateEntitySchemaForCPS(schema) {
  const pilotGridNameArray = ['Account', 'Campaign', 'AdGroup', 'ProductGroup'];
  let pilotProperty = '';
  if (_.contains(pilotGridNameArray, schema.name)) { pilotProperty = 'cpsreporting'; }

  enableFilterForPilotedPerformanceMetrics(schema, pilotProperty, [
    'AverageCPS',
    'SalesCount',
  ]);
  return schema;
}

function updateEntitySchemaForInstallColumns(schema) {
  const pilotGridNameArray = ['Account', 'Campaign', 'AdGroup', 'ProductGroup'];
  let pilotProperty = '';
  if (_.contains(pilotGridNameArray, schema.name)) { pilotProperty = 'installcolumns'; }
  enableFilterForPilotedPerformanceMetrics(schema, pilotProperty, [
    'Installs',
    'CostPerInstall',
    'RevenuePerInstall',
  ]);
  return schema;
}

export function enableForAvgPositionDeprecationPhase1Feature(schema) {
  enableFilterForPilotedPerformanceMetrics(schema, 'AvgPositionDeprecationPhase1', [
    'AbsoluteTopImpressionRate',
    'TopImpressionRate',
  ]);
  return schema;
}

function enableFilterForPilotProperties(schema, propertyNameArray) {
  const schemaCopy = schema;
  _.each(propertyNameArray, (propertyName) => {
    if (getFeaturePermissionByPropertyName(propertyName)
      && !_.isEmpty(schemaCopy.properties[propertyName])) {
      schemaCopy.properties[propertyName].filterable = true;
    }
  });

  return schema;
}


const PARTIAL_CONVERSIONS_ENABLED_ENTITIES = [
  'Campaign',
  'AdGroup',
  'Ad',
  'Keyword',
  'SearchTerm',
  'CampaignCriterionGridData',
  'FeedItem',
  'DsaTarget',
  'DsaSearchTerm',
  'AdExtensionAssociation',
  'PortfolioBidStrategy',
  'BiddableCampaignCriterion',
  'BiddableAdGroupCriterion',
  // Dimension Report
  'DimensionLanding',
  'DimensionAgeGender',
  'DimensionSearchTerm',
  'DimensionPublisher',
  'DimensionCallForwarding',
  'Time',
  'KeywordLabelReport',
  'AdLabelReport',
  'AdGroupLabelReport',
  'CampaignLabelReport',
  'DimensionGeographic',
  'DimensionLocation',
  'DimensionShopping',
  // Audience Association Page
  'AudienceCriterionWithBI',
  'ProductOffer',
  // Conversion Goal Page
  'Goal',
  // Shared Budget Page
  'Budget',
  'Account',
  'ProductGroup',
  'AssetWithBIData',
];

function updatePerformanceMetricsForPartialConversions(
  schema,
  useNewParameters
) {
  const schemaCopy = schema;
  const performanceMetrics = putil.get(schemaCopy, 'properties.PerformanceMetrics');
  const performanceMetricsProperties = putil.get(performanceMetrics, 'schema.properties');
  if (performanceMetricsProperties) {
    const filterableArray = performanceMetrics.filterable;
    const hasFilterableArray = _.isArray(filterableArray);
    each(partialConversionPairs, (value, key) => {
      const propertyNameToDisable = useNewParameters ? key : value;
      const propertyNameToEnable = useNewParameters ? value : key;
      const propertyToDisable = performanceMetricsProperties[propertyNameToDisable];
      const propertyToEnable = performanceMetricsProperties[propertyNameToEnable];

      const isDisablePropFilterable = _.contains(filterableArray, propertyNameToDisable)
        || (propertyToDisable && propertyToDisable.filterable);
      const isEnablePropFilterable = _.contains(filterableArray, propertyNameToEnable)
        || (propertyToEnable && propertyToEnable.filterable);

      if (isDisablePropFilterable || isEnablePropFilterable) {
        if (hasFilterableArray) {
          performanceMetrics.filterable = _.chain(performanceMetrics.filterable)
            .difference([propertyNameToDisable])
            .union([propertyNameToEnable])
            .value();
        }
        if (propertyToDisable) {
          propertyToDisable.filterable = false;
        }
        if (propertyToEnable) {
          propertyToEnable.filterable = true;
        }
      }
    });
  }
}
// This function is a safe guard for grids in case
// both old and new property were both enable by accident
export function updateEntitySchemaForPartialConversions(schema) {
  const isPartialConversionsEnbled = _.result(Permissions.dynamic, 'PartialConversions');
  const isColumnConversionEnabled = _.result(Permissions.dynamic, 'ColumnConversion');
  const enabledEntity = _.contains(PARTIAL_CONVERSIONS_ENABLED_ENTITIES, schema.name);
  const useNewParameters = isColumnConversionEnabled && enabledEntity && isPartialConversionsEnbled;

  updatePerformanceMetricsForPartialConversions(schema, useNewParameters);
  return schema;
}

export function disableFilterForExtraPerformanceMetrics(schema, disabledPerformanceColumns) {
  return updateFilterableForExtraPerformanceMetrics(schema, disabledPerformanceColumns, false);
}

export function updateEntitySchemaForPilotFeatures(originalSchema, isForVNext, filterExpressions = null, enableAvgPositionDeprecation) { // eslint-disable-line max-len
  const schema = putil.cloneDeep(originalSchema);

  // first enable properties (filter type) for features in pilot
  updateEntitySchemaForSOV(schema, false);
  if (isForVNext) {
    updateEntitySchemaForCustomColumn(schema, false, filterExpressions);
    enableAndUpdateFilterForAvailableInsightTypeIds(schema);
  }
  enableForAudienceSOVFeatures(schema);
  if (enableAvgPositionDeprecation) {
    updateEntitySchemaForRemovedPerformanceMetrics(schema, false, filterExpressions);
  }
  enableForExperimentFeature(schema);
  enableForPortfolioBidStrategyFeature(schema);
  enableForViewThroughConversionsFeature(schema);
  enableForConversionValueRuleFeature(schema);
  updateEntitySchemaForMainGoalSelectionFeature(schema);
  updateEntitySchemaForVideoAdsFeature(schema);
  updateEntitySchemaForAppCampaign(schema);
  updateEntitySchemaForCPS(schema);
  updateEntitySchemaForInstallColumns(schema);
  enableFilterForPilotProperties(schema, ['CampaignType', 'Language', 'AssociationsCount', 'TrackingStatus', 'IsExperiment', 'AdsByBing', 'CpmBid', 'CpvBid', 'BidStrategyName', 'CampaignMigrationStatus']);

  // enable enum values (filter value set) for features in pilot
  updateEnumValueSets(schema);

  return schema;
}
